import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import Images from '@/assets/Images';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
  Chip,
  Dialog,
  Link,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useCompletePaginationNavigation } from '@/utils/autoPaginationNavigation';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  QrCode as QrCodeIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import rfidService, { Vehicle } from '../../../../services/rfidService';
import toast from '../../../../utils/toast';
import VehicleMovementHistory from '../VehicleMovementHistory';

const VehicleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [epcDialogOpen, setEpcDialogOpen] = useState<boolean>(false);
  const [newEpcId, setNewEpcId] = useState<string>('');
  const [updatingEpc, setUpdatingEpc] = useState<boolean>(false);
  const [epcIdError, setEpcIdError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { backToListing, toEdit } = useCompletePaginationNavigation(
    navigate,
    '/rfid/vehicles'
  );
  useEffect(() => {
    if (id) {
      fetchVehicle(id);
    }
  }, [id]);

  const fetchVehicle = async (vehicleId: string) => {
    try {
      setLoading(true);
      const data = await rfidService.getVehicleById(vehicleId);
      setVehicle(data);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      toast.error('Failed to fetch vehicle details');
    } finally {
      setLoading(false);
    }
  };

  const handleEpcDialogOpen = () => {
    setNewEpcId(vehicle?.epc_id || '');
    setEpcDialogOpen(true);
  };

  const handleEpcDialogClose = () => {
    setEpcDialogOpen(false);
    setEpcIdError(null);
  };

  // const validateEpcId = (epcId: string): boolean => {
  //   // If empty, clear error and return true (valid)
  //   if (!epcId) {
  //     setEpcIdError(null);
  //     return true;
  //   }

  //   // Check if it's a valid hexadecimal string
  //   if (!/^[0-9A-Fa-f]+$/.test(epcId)) {
  //     setEpcIdError('EPC-ID must be a hexadecimal string (0-9, A-F)');
  //     return false;
  //   }

  //   // Check the length
  //   if (epcId.length < 16 || epcId.length > 24) {
  //     setEpcIdError('EPC-ID must be between 16 and 24 characters long');
  //     return false;
  //   }

  //   // Check if it starts with a valid header
  //   const header = epcId.substring(0, 2).toUpperCase();
  //   const validHeaders = ['30', '31', '32', '33', '34', '35', '36', '37', '38', '39'];
  //   if (!validHeaders.includes(header)) {
  //     setEpcIdError('EPC-ID must start with a valid header (30-39)');
  //     return false;
  //   }

  //   // Clear error if all validations pass
  //   setEpcIdError(null);
  //   return true;
  // };

  const handleEpcUpdate = async () => {
    if (!id) return;

    // // Validate EPC ID before submitting
    // if (!validateEpcId(newEpcId)) {
    //   return;
    // }

    try {
      setUpdatingEpc(true);
      await rfidService.patchVehicle(id, { epc_id: newEpcId });
      toast.success('Vehicle EPC updated successfully');
      fetchVehicle(id);
      setEpcDialogOpen(false);
      setEpcIdError(null);
    } catch (error) {
      console.error('Error updating vehicle EPC:', error);
      toast.error('Failed to update vehicle EPC');
    } finally {
      setUpdatingEpc(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!vehicle) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Vehicle not found
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/rfid/vehicles"
          sx={{ mt: 2 }}
        >
          Back to Vehicles
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box display="flex" alignItems="center">
          <h3 className="pageTitle">
            <Link onClick={() => backToListing()} className="back">
              <img src={Images.BACK} alt="Back" width="22" />
            </Link>
            Vehicle Details
          </h3>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<EditIcon />}
          component={RouterLink}
          to={`/rfid/vehicles/${id}/edit`}
          sx={{ mr: 2 }}
        >
          Edit Vehicle
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h5" gutterBottom>
                {vehicle.name}
              </Typography>
              <Typography
                variant="subtitle1"
                color="textSecondary"
                gutterBottom
              >
                Registration: {vehicle.registration_number}
              </Typography>
              {vehicle.manufacturer_detail?.name && (
                <Typography variant="body1" gutterBottom>
                  Manufacturer: {vehicle.manufacturer_detail.name}
                </Typography>
              )}
              {vehicle.model_detail?.name && (
                <Typography variant="body1" gutterBottom>
                  Model: {vehicle.model_detail.name}
                </Typography>
              )}
              {vehicle.description && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {vehicle.description}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    RFID Tag
                  </Typography>
                  {vehicle.epc_id ? (
                    <Chip
                      icon={<QrCodeIcon />}
                      label={vehicle.epc_id}
                      sx={{
                        mb: 1,
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        borderColor: '#2e7d32',
                        '& .MuiChip-icon': { color: '#2e7d32' },
                        fontSize: '1rem',
                        padding: '8px 4px',
                      }}
                    />
                  ) : (
                    <Chip
                      label="No Tag Assigned"
                      color="default"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                  )}
                  <Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<LinkIcon />}
                      onClick={handleEpcDialogOpen}
                      sx={{ mt: 1 }}
                    >
                      {vehicle.epc_id ? 'Change Tag' : 'Assign Tag'}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <VehicleMovementHistory vehicleId={id || ''} />

      <Dialog
        open={epcDialogOpen}
        onClose={handleEpcDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {vehicle.epc_id ? 'Update RFID Tag' : 'Assign RFID Tag'}
        </DialogTitle>

        <DialogContent
          sx={{
            overflow: 'visible',
            pt: 3,
          }}
        >
          <TextField
            autoFocus
            margin="dense"
            label="EPC ID"
            fullWidth
            value={newEpcId}
            InputLabelProps={{
              shrink: true,
            }}
            onChange={(e) => {
              setNewEpcId(e.target.value);
              // validateEpcId(e.target.value);
            }}
            error={!!epcIdError}
            helperText={
              epcIdError ||
              'Enter the EPC ID of the RFID tag (hexadecimal, 16-24 chars, starts with 30-39)'
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleEpcDialogClose}>Cancel</Button>

          <Button
            onClick={handleEpcUpdate}
            color="primary"
            disabled={updatingEpc}
          >
            {updatingEpc ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VehicleDetail;
